// 🔧 store_variants.js
// ✅ Purpose: Manager-facing Store variant and inventory ledger management page
// ✅ Features:
// - Manages product variants under /events/{eventId}/store_items/{productId}/variants
// - Tracks price, COGS, cost of sale, received, available, sold, reserved inventory
// - Adds inventory ledger logs under /variants/{variantId}/inventory_logs
// - Supports restock, adjustment, damage, and return inventory actions
// - Automatically updates variant inventory counts from ledger actions
// - Updates parent product inventory totals after variant changes
// - Shows success and error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { Link, useParams } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import { resolvePowerupSnapshot } from "../../utils/powerup_urls";

const StoreVariants = () => {
  const { productId } = useParams();

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const storeEnabled = hasPurchasedAddOn(
    eventDoc,
    "store"
  );

  const emptyVariantForm = useMemo(
    () => ({
      label: "",
      size: "",
      fit: "Unisex",
      color: "",
      sku: "",
      priceDollars: "",
      costOfGoodsDollars: "",
      costOfSaleDollars: "",
      receivedInventory: 0,
      availableInventory: 0,
      soldInventory: 0,
      reservedInventory: 0,
      active: true,
    }),
    []
  );

  const emptyLedgerForm = useMemo(
    () => ({
      variantId: "",
      type: "restock",
      quantity: 0,
      note: "",
    }),
    []
  );

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [inventoryLogs, setInventoryLogs] = useState([]);
  const [form, setForm] = useState(emptyVariantForm);
  const [ledgerForm, setLedgerForm] = useState(emptyLedgerForm);
  const [editingVariantId, setEditingVariantId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolvedProductId, setResolvedProductId] = useState("");
  const productRecordId = resolvedProductId || productId;

  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!status && !error) return;

    const timer = setTimeout(() => {
      setStatus(null);
      setError(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status, error]);

  const dollarsToCents = (value) => Math.round(Number(value || 0) * 100);

  const centsToDollars = (value) => {
    const numeric = Number(value || 0);
    return numeric > 0 ? (numeric / 100).toFixed(2) : "";
  };

  const updateProductInventoryTotals = async () => {
    const variantsSnap = await getDocs(
      collection(db, "events", eventId, "store_items", productRecordId, "variants")
    );

    const variantList = variantsSnap.docs.map((docSnap) => docSnap.data());
    const prices = variantList
      .map((variant) => Number(variant.priceCents || 0))
      .filter((price) => price >= 0);

    const totals = variantList.reduce(
      (summary, variant) => ({
        totalAvailableInventory:
          summary.totalAvailableInventory + Number(variant.availableInventory || 0),
        totalReceivedInventory:
          summary.totalReceivedInventory + Number(variant.receivedInventory || 0),
        totalSoldInventory:
          summary.totalSoldInventory + Number(variant.soldInventory || 0),
        totalReservedInventory:
          summary.totalReservedInventory + Number(variant.reservedInventory || 0),
      }),
      {
        totalAvailableInventory: 0,
        totalReceivedInventory: 0,
        totalSoldInventory: 0,
        totalReservedInventory: 0,
      }
    );

    await updateDoc(doc(db, "events", eventId, "store_items", productRecordId), {
      ...totals,
      hasVariants: variantList.length > 0,
      minPriceCents: prices.length ? Math.min(...prices) : 0,
      maxPriceCents: prices.length ? Math.max(...prices) : 0,
      updatedAt: serverTimestamp(),
    });
  };

  const loadProductAndVariants = async () => {
    if (!eventId || !productId) return;

    setLoading(true);

    try {
      const productSnap = await resolvePowerupSnapshot(db, eventId, "product", productId);

      if (!productSnap?.exists()) {
        setError("Store product was not found.");
        setLoading(false);
        return;
      }
      const canonicalProductId = productSnap.id;
      setResolvedProductId(canonicalProductId);

      setProduct({
        id: productSnap.id,
        ...productSnap.data(),
      });

      const variantsSnap = await getDocs(
        query(
          collection(db, "events", eventId, "store_items", canonicalProductId, "variants"),
          orderBy("label", "asc")
        )
      );

      const variantList = variantsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setVariants(variantList);

      const logResults = [];

      for (const variant of variantList) {
        const logsSnap = await getDocs(
          query(
            collection(
              db,
              "events",
              eventId,
              "store_items",
              canonicalProductId,
              "variants",
              variant.id,
              "inventory_logs"
            ),
            orderBy("createdAt", "desc")
          )
        );

        logsSnap.docs.forEach((logDoc) => {
          logResults.push({
            id: logDoc.id,
            variantId: variant.id,
            variantLabel: variant.label,
            ...logDoc.data(),
          });
        });
      }

      setInventoryLogs(logResults);

      logger.log("✅ Store variants and inventory logs loaded", {
        eventId,
        productId: canonicalProductId,
        variants: variantList.length,
        logs: logResults.length,
      });
    } catch (err) {
      logger.error("❌ Failed loading Store variants:", {
        value: err,
      });

      setError("Unable to load Store variants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || !eventId || !productId) return;
    setResolvedProductId("");
    loadProductAndVariants();
  }, [ready, eventId, productId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLedgerChange = (e) => {
    const { name, value } = e.target;

    setLedgerForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setStatus(null);
    setError(null);

    const label = String(form.label || "").trim();

    if (!label) {
      setError("Variant label is required.");
      return;
    }

    const receivedInventory = Number(form.receivedInventory || 0);
    const availableInventory = Number(form.availableInventory || 0);
    const soldInventory = Number(form.soldInventory || 0);
    const reservedInventory = Number(form.reservedInventory || 0);
    const inventoryValues = [receivedInventory, availableInventory, soldInventory, reservedInventory];

    if (inventoryValues.some((value) => !Number.isInteger(value) || value < 0)) {
      setError("Inventory quantities must be whole numbers of zero or more.");
      return;
    }

    if (availableInventory + soldInventory + reservedInventory > receivedInventory) {
      setError("Available, sold, and reserved inventory cannot exceed received inventory.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        label,
        size: String(form.size || "").trim(),
        fit: String(form.fit || "").trim(),
        color: String(form.color || "").trim(),
        sku: String(form.sku || "").trim(),
        priceCents: dollarsToCents(form.priceDollars),
        costOfGoodsCents: dollarsToCents(form.costOfGoodsDollars),
        costOfSaleCents: dollarsToCents(form.costOfSaleDollars),
        receivedInventory,
        availableInventory,
        soldInventory,
        reservedInventory,
        active: form.active === true,
        updatedAt: serverTimestamp(),
      };

      if (editingVariantId) {
        await updateDoc(
          doc(db, "events", eventId, "store_items", productRecordId, "variants", editingVariantId),
          payload
        );

        setStatus("✅ Variant updated successfully.");
      } else {
        await addDoc(
          collection(db, "events", eventId, "store_items", productRecordId, "variants"),
          {
            ...payload,
            createdAt: serverTimestamp(),
          }
        );

        setStatus("✅ Variant created successfully.");
      }

      await updateProductInventoryTotals();
      setForm(emptyVariantForm);
      setEditingVariantId("");
      await loadProductAndVariants();
    } catch (err) {
      logger.error("❌ Failed saving Store variant:", {
        value: err,
      });

      setError("Unable to save Store variant.");
    } finally {
      setSaving(false);
    }
  };

  const applyInventoryLedger = async (e) => {
    e.preventDefault();

    setStatus(null);
    setError(null);

    const variantId = ledgerForm.variantId;
    const type = ledgerForm.type;
    const quantity = Number(ledgerForm.quantity || 0);

    if (!variantId) {
      setError("Please select a variant.");
      return;
    }

    if (!quantity || quantity <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }

    const selectedVariant = variants.find((variant) => variant.id === variantId);

    if (!selectedVariant) {
      setError("Selected variant was not found.");
      return;
    }

    let inventoryUpdate = {};

    if (type === "restock") {
      inventoryUpdate = {
        receivedInventory: increment(quantity),
        availableInventory: increment(quantity),
      };
    }

    if (type === "return") {
      if (Number(selectedVariant.soldInventory || 0) < quantity) {
        setError("Return quantity cannot be greater than sold inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(quantity),
        soldInventory: increment(-quantity),
      };
    }

    if (type === "damage") {
      if (Number(selectedVariant.availableInventory || 0) < quantity) {
        setError("Damage quantity cannot be greater than available inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(-quantity),
      };
    }

    if (type === "adjustment_add") {
      inventoryUpdate = {
        receivedInventory: increment(quantity),
        availableInventory: increment(quantity),
      };
    }

    if (type === "adjustment_remove") {
      if (Number(selectedVariant.availableInventory || 0) < quantity) {
        setError("Adjustment quantity cannot be greater than available inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(-quantity),
      };
    }

    if (type === "reserve") {
      if (Number(selectedVariant.availableInventory || 0) < quantity) {
        setError("Reserved quantity cannot be greater than available inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(-quantity),
        reservedInventory: increment(quantity),
      };
    }

    if (type === "release_reservation") {
      if (Number(selectedVariant.reservedInventory || 0) < quantity) {
        setError("Release quantity cannot be greater than reserved inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(quantity),
        reservedInventory: increment(-quantity),
      };
    }

    if (type === "external_sale") {
      if (Number(selectedVariant.availableInventory || 0) < quantity) {
        setError("Cash-sale quantity cannot be greater than available inventory.");
        return;
      }

      inventoryUpdate = {
        availableInventory: increment(-quantity),
        soldInventory: increment(quantity),
      };
    }

    try {
      const variantRef = doc(
        db,
        "events",
        eventId,
        "store_items",
        productRecordId,
        "variants",
        variantId
      );

      await updateDoc(variantRef, {
        ...inventoryUpdate,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(variantRef, "inventory_logs"), {
        type,
        quantity,
        note: String(ledgerForm.note || "").trim(),
        variantId,
        variantLabel: selectedVariant.label || "",
        createdAt: serverTimestamp(),
      });

      await updateProductInventoryTotals();

      setLedgerForm(emptyLedgerForm);
      setStatus("✅ Inventory ledger updated.");
      await loadProductAndVariants();
    } catch (err) {
      logger.error("❌ Failed applying inventory ledger:", {
        value: err,
      });

      setError("Unable to update inventory ledger.");
    }
  };

  const startEdit = (variant) => {
    setEditingVariantId(variant.id);

    setForm({
      label: variant.label || "",
      size: variant.size || "",
      fit: variant.fit || "Unisex",
      color: variant.color || "",
      sku: variant.sku || "",
      priceDollars: centsToDollars(variant.priceCents),
      costOfGoodsDollars: centsToDollars(variant.costOfGoodsCents),
      costOfSaleDollars: centsToDollars(variant.costOfSaleCents),
      receivedInventory: Number(variant.receivedInventory || 0),
      availableInventory: Number(variant.availableInventory || 0),
      soldInventory: Number(variant.soldInventory || 0),
      reservedInventory: Number(variant.reservedInventory || 0),
      active: variant.active !== false,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingVariantId("");
    setForm(emptyVariantForm);
  };

  const handleDelete = async (variant) => {
    const confirmed = window.confirm(`Delete variant "${variant.label}"?`);

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "events", eventId, "store_items", productRecordId, "variants", variant.id)
      );

      await updateProductInventoryTotals();
      setStatus("✅ Variant deleted.");
      await loadProductAndVariants();
    } catch (err) {
      logger.error("❌ Failed deleting Store variant:", {
        value: err,
      });

      setError("Unable to delete Store variant.");
    }
  };

  const totalAvailable = variants.reduce(
    (sum, variant) => sum + Number(variant.availableInventory || 0),
    0
  );

  const totalReceived = variants.reduce(
    (sum, variant) => sum + Number(variant.receivedInventory || 0),
    0
  );

  if (
    ready &&
    eventDoc &&
    !storeEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Event Merch Store Add-on Required"
        description="Purchase the Event Merch Store add-on before managing Store variants and inventory."
      />
    );
  }

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store Variants..." />;
  }
  
  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🧢 Store Variants</h1>
            <p className="lead text-muted mb-0">
              {product?.name || "Store Product"} — {totalAvailable} / {totalReceived} available
            </p>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <strong>{editingVariantId ? "✏️ Edit Variant" : "➕ Add Variant"}</strong>
          </div>

          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Variant Label *</label>
                  <input name="label" className="form-control" value={form.label} onChange={handleChange} required />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Size</label>
                  <input name="size" className="form-control" value={form.size} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Fit</label>
                  <select name="fit" className="form-select" value={form.fit} onChange={handleChange}>
                    <option value="Unisex">Unisex</option>
                    <option value="Mens">Mens</option>
                    <option value="Womens">Womens</option>
                    <option value="Youth">Youth</option>
                    <option value="Kids">Kids</option>
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">Color</label>
                  <input name="color" className="form-control" value={form.color} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">SKU</label>
                  <input name="sku" className="form-control" value={form.sku} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">Price</label>
                  <input type="number" step="0.01" name="priceDollars" className="form-control" value={form.priceDollars} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Cost of Goods</label>
                  <input type="number" step="0.01" name="costOfGoodsDollars" className="form-control" value={form.costOfGoodsDollars} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Cost of Sale</label>
                  <input type="number" step="0.01" name="costOfSaleDollars" className="form-control" value={form.costOfSaleDollars} onChange={handleChange} />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Received</label>
                  <input type="number" name="receivedInventory" className="form-control" value={form.receivedInventory} onChange={handleChange} min="0" />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Available</label>
                  <input type="number" name="availableInventory" className="form-control" value={form.availableInventory} onChange={handleChange} min="0" />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Sold</label>
                  <input type="number" name="soldInventory" className="form-control" value={form.soldInventory} onChange={handleChange} min="0" />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Reserved</label>
                  <input type="number" name="reservedInventory" className="form-control" value={form.reservedInventory} onChange={handleChange} min="0" />
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingVariantId ? "💾 Save Variant" : "➕ Add Variant"}
                </button>

                {editingVariantId && (
                  <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit}>
                    Cancel Edit
                  </button>
                )}

                <Link to={`/${eventslug}/store_products`} className="btn btn-outline-dark">
                  Back to Products
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <strong>📒 Inventory Ledger Action</strong>
          </div>

          <div className="card-body">
            <form onSubmit={applyInventoryLedger}>
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label">Variant</label>
                  <select name="variantId" className="form-select" value={ledgerForm.variantId} onChange={handleLedgerChange} required>
                    <option value="">Select variant</option>
                    {variants.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.label} — {Number(variant.availableInventory || 0)} / {Number(variant.receivedInventory || 0)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Action</label>
                  <select name="type" className="form-select" value={ledgerForm.type} onChange={handleLedgerChange}>
                    <option value="restock">Restock</option>
                    <option value="return">Return</option>
                    <option value="damage">Damage</option>
                    <option value="adjustment_add">Manual Add</option>
                    <option value="adjustment_remove">Manual Remove</option>
                    <option value="reserve">Reserve for External Order</option>
                    <option value="release_reservation">Release Reservation</option>
                    <option value="external_sale">Record Cash / External Sale</option>
                  </select>
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label">Quantity</label>
                  <input type="number" name="quantity" className="form-control" value={ledgerForm.quantity} onChange={handleLedgerChange} min="1" required />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Note</label>
                  <input name="note" className="form-control" value={ledgerForm.note} onChange={handleLedgerChange} placeholder="Optional note" />
                </div>
              </div>

              <button type="submit" className="btn btn-success mt-3">
                Apply Inventory Action
              </button>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex justify-content-between">
            <strong>📦 Variant Inventory</strong>
            <span className="badge bg-dark">{variants.length} Variant{variants.length === 1 ? "" : "s"}</span>
          </div>

          <div className="card-body">
            {variants.length === 0 ? (
              <div className="alert alert-info mb-0">No variants have been added yet.</div>
            ) : (
              <div className="row g-4">
                {variants.map((variant) => (
                  <div key={variant.id} className="col-12 col-md-6 col-xl-4">
                    <div className="card h-100 border shadow-sm">
                      <div className="card-body">
                        <h5 className="fw-bold mb-1">{variant.label}</h5>
                        <div className="small text-muted mb-3">{variant.sku || "No SKU"}</div>

                        <div className="border rounded bg-light p-3 small mb-3">
                          <div><strong>Available:</strong> {Number(variant.availableInventory || 0)} / {Number(variant.receivedInventory || 0)}</div>
                          <div><strong>Sold:</strong> {Number(variant.soldInventory || 0)}</div>
                          <div><strong>Reserved:</strong> {Number(variant.reservedInventory || 0)}</div>
                          <hr />
                          <div><strong>Price:</strong> ${(Number(variant.priceCents || 0) / 100).toFixed(2)}</div>
                          <div><strong>COGS:</strong> ${(Number(variant.costOfGoodsCents || 0) / 100).toFixed(2)}</div>
                          <div><strong>Cost of Sale:</strong> ${(Number(variant.costOfSaleCents || 0) / 100).toFixed(2)}</div>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => startEdit(variant)}>
                            Edit
                          </button>

                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(variant)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {inventoryLogs.length > 0 && (
              <div className="mt-4">
                <h5 className="fw-bold">📒 Inventory History</h5>

                <div className="list-group">
                  {inventoryLogs.slice(0, 25).map((log) => (
                    <div key={`${log.variantId}-${log.id}`} className="list-group-item">
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                        <div>
                          <strong>{log.variantLabel}</strong>
                          <div className="small text-muted">
                            {log.type} — Qty {log.quantity}
                            {log.note ? ` — ${log.note}` : ""}
                          </div>
                        </div>

                        <div className="small text-muted">
                          {log.createdAt?.toDate
                            ? log.createdAt.toDate().toLocaleString()
                            : "Recently"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreVariants;
